import React, { useState } from 'react';
import { Card, Tabs, Typography, Form, Input, Button, message, Modal } from 'antd';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoginForm from '../components/auth/LoginForm';
import RegisterForm from '../components/auth/RegisterForm';
import Logo from '../components/common/Logo';
import { auth } from '../services/api';

const { Title, Paragraph } = Typography;

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f0f2f5;
`;

const WelcomeSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 48px;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  color: white;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 48px;
  cursor: pointer;
  
  span {
    font-size: 24px;
    font-weight: bold;
    color: white;
  }
  
  &:hover {
    opacity: 0.9;
  }
`;

const WelcomeTitle = styled(Title)`
  color: white !important;
  margin-bottom: 24px !important;
  text-align: center;
`;

const WelcomeText = styled(Paragraph)`
  color: rgba(255, 255, 255, 0.85) !important;
  font-size: 16px;
  text-align: center;
  max-width: 600px;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const AuthView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'login';
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
  const [forgotPasswordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSuccess = () => {
    navigate('/app');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleForgotPassword = async (values: any) => {
    try {
      setLoading(true);
      await auth.forgotPassword(values.email);
      message.success('重置密码链接已发送到您的邮箱');
      setForgotPasswordVisible(false);
      forgotPasswordForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.detail || '发送失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <WelcomeSection>
        <LogoContainer onClick={handleLogoClick}>
          <Logo size={48} borderColor="#fff" />
          <span>来创业</span>
        </LogoContainer>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 32, color: '#fff' }}>
          欢迎使用来创业（LetStartUp）
        </Title>
        <Paragraph style={{ textAlign: 'center', marginBottom: 32, color: 'rgba(255, 255, 255, 0.85)' }}>
          来创业（LetStartUp）是你的创业伙伴，我们将帮助你一步步实现创业目标。
        </Paragraph>
      </WelcomeSection>

      <FormSection>
        <StyledCard>
          <Tabs
            activeKey={defaultTab}
            onChange={(key) => {
              navigate(`/auth?tab=${key}`);
            }}
            items={[
              {
                key: 'login',
                label: '登录',
                children: (
                  <LoginForm
                    onSuccess={handleSuccess}
                    onSwitchToRegister={() => navigate('/auth?tab=register')}
                    onForgotPassword={() => setForgotPasswordVisible(true)}
                  />
                ),
              },
              {
                key: 'register',
                label: '注册',
                children: (
                  <RegisterForm
                    onSuccess={handleSuccess}
                    onSwitchToLogin={() => navigate('/auth?tab=login')}
                  />
                ),
              },
            ]}
          />
        </StyledCard>
      </FormSection>

      <Modal
        title="忘记密码"
        open={forgotPasswordVisible}
        onCancel={() => setForgotPasswordVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={forgotPasswordForm}
          layout="vertical"
          onFinish={handleForgotPassword}
          preserve={false}
        >
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="请输入您注册时使用的邮箱" />
          </Form.Item>
          
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              发送重置链接
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Container>
  );
};

export default AuthView; 